import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Menu,
  Stack,
  TextField,
  Tooltip,
  Checkbox,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  ACTIVE_STATUSES,
  ATTENTION_NEEDED_STATUSES,
  ALL_STATUSES,
} from "../common/statusColors";

const SEVERITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

const SORT_OPTIONS = [
  { value: "urgency:desc", label: "Most urgent" },
  { value: "created_at:desc", label: "Newest" },
  { value: "created_at:asc", label: "Oldest" },
  { value: "scheduled_at:asc", label: "Scheduled soonest" },
  { value: "severity:desc", label: "Highest severity" },
  { value: "check_title:asc", label: "Check name (A→Z)" },
];

/**
 * Top-of-list filter and search bar. Filter chips map directly to API query
 * params; selecting a chip mutates the parent's filter state which is then
 * synced into the URL so the view is shareable.
 */
const FilterBar = ({
  filters,
  onChange,
  onSearchChange,
  onReset,
  searchValue = "",
  onSearchSubmit,
  onNewRequest,
  remediableCheckIds = [],
  accountOptions = [],
  serviceOptions = [],
  onRefresh,
  refreshing,
}) => {
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [severityAnchor, setSeverityAnchor] = useState(null);

  const statusSet = new Set((filters.status_in || "").split(",").filter(Boolean));
  const toggleStatus = (status) => {
    const next = new Set(statusSet);
    if (next.has(status)) next.delete(status);
    else next.add(status);
    onChange({ status_in: Array.from(next).join(",") || null });
  };

  const applyStatusPreset = (preset) => {
    if (preset === "attention") onChange({ status_in: ATTENTION_NEEDED_STATUSES.join(",") });
    else if (preset === "active") onChange({ status_in: ACTIVE_STATUSES.join(",") });
    else if (preset === "terminal") onChange({ status_in: "SUCCEEDED,FAILED,CANCELLED" });
    else onChange({ status_in: null });
    setStatusAnchor(null);
  };

  const activeFilterChips = [];
  if (filters.status_in)
    activeFilterChips.push(
      ...filters.status_in.split(",").filter(Boolean).map((s) => ({
        key: `status:${s}`,
        label: `Status: ${s.replace(/_/g, " ").toLowerCase()}`,
        clear: () => toggleStatus(s),
      }))
    );
  if (filters.severity)
    activeFilterChips.push({
      key: "severity",
      label: `Severity: ${filters.severity}`,
      clear: () => onChange({ severity: null }),
    });
  if (filters.service)
    activeFilterChips.push({
      key: "service",
      label: `Service: ${filters.service}`,
      clear: () => onChange({ service: null }),
    });
  if (filters.account)
    activeFilterChips.push({
      key: "account",
      label: `Account: ${filters.account}`,
      clear: () => onChange({ account: null }),
    });
  if (filters.check)
    activeFilterChips.push({
      key: "check",
      label: `Check: ${filters.check}`,
      clear: () => onChange({ check: null }),
    });

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <TextField
          size="small"
          placeholder="Search check, resource, requester…"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSearchSubmit?.();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    onSearchChange("");
                    onSearchSubmit?.("");
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ width: { xs: "100%", md: 320 } }}
        />

        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterListIcon />}
          onClick={(e) => setStatusAnchor(e.currentTarget)}
        >
          Status
          {filters.status_in ? ` (${statusSet.size})` : ""}
        </Button>
        <Menu open={Boolean(statusAnchor)} anchorEl={statusAnchor} onClose={() => setStatusAnchor(null)}>
          <ListSubheader>Presets</ListSubheader>
          <MenuItem onClick={() => applyStatusPreset("attention")}>Needs attention</MenuItem>
          <MenuItem onClick={() => applyStatusPreset("active")}>Active</MenuItem>
          <MenuItem onClick={() => applyStatusPreset("terminal")}>Terminal</MenuItem>
          <MenuItem onClick={() => applyStatusPreset("clear")}>Clear status filter</MenuItem>
          <ListSubheader>All statuses</ListSubheader>
          {ALL_STATUSES.map((s) => (
            <MenuItem key={s} dense onClick={() => toggleStatus(s)}>
              <Checkbox size="small" checked={statusSet.has(s)} sx={{ mr: 1 }} />
              {s.replace(/_/g, " ").toLowerCase()}
            </MenuItem>
          ))}
        </Menu>

        <Button
          variant="outlined"
          size="small"
          onClick={(e) => setSeverityAnchor(e.currentTarget)}
        >
          Severity{filters.severity ? `: ${filters.severity}` : ""}
        </Button>
        <Menu open={Boolean(severityAnchor)} anchorEl={severityAnchor} onClose={() => setSeverityAnchor(null)}>
          <MenuItem onClick={() => { onChange({ severity: null }); setSeverityAnchor(null); }}>
            All severities
          </MenuItem>
          {SEVERITIES.map((s) => (
            <MenuItem
              key={s}
              onClick={() => { onChange({ severity: s }); setSeverityAnchor(null); }}
            >
              {s}
            </MenuItem>
          ))}
        </Menu>

        {serviceOptions.length > 0 && (
          <TextField
            select
            size="small"
            label="Service"
            value={filters.service || ""}
            onChange={(e) => onChange({ service: e.target.value || null })}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            {serviceOptions.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        )}

        {accountOptions.length > 0 && (
          <TextField
            select
            size="small"
            label="Account"
            value={filters.account || ""}
            onChange={(e) => onChange({ account: e.target.value || null })}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All accounts</MenuItem>
            {accountOptions.map((a) => (
              <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          size="small"
          label="Sort"
          value={filters.sort || "urgency:desc"}
          onChange={(e) => onChange({ sort: e.target.value })}
          sx={{ minWidth: 200 }}
        >
          {SORT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Refresh now">
          <span>
            <IconButton onClick={onRefresh} disabled={refreshing} size="small">
              <RefreshIcon
                sx={{
                  animation: refreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        {onNewRequest && (
          <Button variant="contained" size="small" onClick={onNewRequest}>
            New request
          </Button>
        )}
      </Stack>

      {activeFilterChips.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
          {activeFilterChips.map((c) => (
            <Chip
              key={c.key}
              label={c.label}
              size="small"
              onDelete={c.clear}
              sx={{ mb: 0.5 }}
            />
          ))}
          <Button size="small" onClick={onReset} sx={{ ml: 1 }}>
            Clear all
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default FilterBar;
